import { mapActions, mapGetters } from "vuex";
import {
  RETRIEVE_CALLS,
  UPDATE_CALL_DETAIL,
  UPDATE_BULK_CALL_DETAILS,
  DELETE_CALL_DETAIL,
  DELETE_BULK_CALLS,
  GET_TASK_STATUS,
  RETRIEVE_PRESIGNED_URL,
  LOGOUT,
} from "@/store/_actiontypes";
import { getDestFullPhoneName } from "@/helpers/Destination_Information";
import { interactionUpdate } from "@/helpers/helper";
import { INTERACTION_POINT } from "@/global/_constants";
import { stopTokenRefreshService } from "@/services/tokenRefreshService";

/**
 * Shared call-list behaviour used by the Digital (HomePage) and Analog
 * (AnalogPage) dashboards: fetching helpers, sorting, formatting, bulk/single
 * delete, audio download and the search-filter plumbing.
 *
 * Each consuming component owns only what actually differs between the two
 * views (its `getCallList` implementation, table `headers`, and the operator
 * sidebar for Digital). Everything common lives here so the two dashboards
 * cannot drift apart again.
 */
export default {
  computed: {
    ...mapGetters("project", ["activeAccountId"]),
  },
  methods: {
    ...mapActions("call", [
      RETRIEVE_CALLS,
      DELETE_CALL_DETAIL,
      UPDATE_CALL_DETAIL,
      UPDATE_BULK_CALL_DETAILS,
      DELETE_BULK_CALLS,
      GET_TASK_STATUS,
    ]),
    ...mapActions("download", [RETRIEVE_PRESIGNED_URL]),
    ...mapActions("account", [LOGOUT]),

    sortDataFunction(sortField) {
      this.headers = this.headers.map((h) => {
        if (h.value === sortField) {
          const newOrder = h.sortOrder === "asc" ? "desc" : "asc";
          interactionUpdate(INTERACTION_POINT.APPLY_FILTER, "table_header_sort", `${sortField}_${newOrder}`);
          return { ...h, sortOrder: newOrder };
        } else {
          const copy = { ...h };
          delete copy.sortOrder;
          return copy;
        }
      });
    },

    headerActiveStatus(status) {
      if (status == "asc" || status == "desc") {
        return "mr-1 active-header";
      } else {
        return "";
      }
    },

    handlePhoneNumberFormat(number) {
      let hasLetter = /[a-zA-Z]/.test(number);
      if (hasLetter) {
        return number || "N/A";
      }
      if (number?.length > 0) {
        let digitsOnly = number?.replace(/\D/g, "");
        if (digitsOnly.length === 4) {
          return getDestFullPhoneName(number);
        }
        if (digitsOnly > 4) {
          number = number?.toString()?.replace(/[-\s]/g, "");
          const groups = [];
          while (number.length > 4) {
            groups.unshift(number.slice(-4));
            number = number.slice(0, -4);
          }
          if (number.length) groups.unshift(number);
          return groups.join("-") || "N/A";
        } else {
          return number || "N/A";
        }
      } else {
        return "N/A";
      }
    },

    async logout() {
      stopTokenRefreshService();
      try {
        if (this.user) {
          let refresh_token = this.user.refresh_token;
          try {
            interactionUpdate(INTERACTION_POINT.LOGOUT, "ログアウト", this.userProfile?.email || "default");
            await this.LOGOUT({ refresh_token });
          } catch (logoutError) {
            console.error("Logout error:", logoutError);
          }
        } else {
          console.warn("No user found to log out.");
        }
      } catch (error) {
        console.error("Error during logout:", error);
      }
    },

    showSnackbar(message, color) {
      this.snackbarMessage = message;
      this.snackbarColor = color;
      this.snackbar = true;
    },

    initiatedCall(item) {
      interactionUpdate(INTERACTION_POINT.CALL_LIST_DETAILS, `表示アイコン`, item.session_id || "default");
      window.open(
        this.$router.resolve({
          path: "/call",
          query: { session_id: item.session_id },
        }).href,
        "_blank"
      );
    },

    refreshCallList() {
      if (this.options.page === 1) {
        this.getCallList();
      } else {
        this.options = { ...this.options, page: 1 };
      }
    },

    getDuration(start, end) {
      const startDate = new Date(start || new Date());
      const endDate = new Date(end || new Date());
      const duration = endDate - startDate;
      const seconds = Math.floor((duration / 1000) % 60);
      const minutes = Math.floor((duration / (1000 * 60)) % 60);
      const hours = Math.floor(duration / (1000 * 60 * 60));
      return `${hours}h:${minutes}m:${seconds}s`;
    },

    deleteCall(session_id) {
      this.deleteDialog = true;
      this.selectedSessionId = session_id;
    },

    deleteBulkCallsFunction() {
      if (!this.isAdmin) return;
      const sessionIds = this.selectedCalls.map((item) => item.session_id);
      if (sessionIds.length) {
        this.isBulkDeleting = true;
        this.DELETE_BULK_CALLS({ session_ids: sessionIds })
          .then((response) => {
            const taskId = response?.detail?.response?.task_id || null;
            if (taskId) {
              this.bulkDeleteTaskId = taskId;
              const sessionIdsLength = sessionIds.length;
              const interactionValue = { sessionIds, sessionIdsLength, taskId };
              interactionUpdate(INTERACTION_POINT.DELETE_BULK_CALLS, `削除アイコン`, `${JSON.stringify(interactionValue)}`);
              this.startPollingTaskStatus(sessionIdsLength);
            } else {
              this.selectedCalls = [];
              this.isBulkDeleting = false;
              this.deleteBulkDialog = false;
              this.refreshCallList();
            }
          })
          .catch((error) => {
            this.isBulkDeleting = false;
            console.error("Error deleting bulk calls:", error);
          });
      }
    },

    startPollingTaskStatus(sessionIdsLength = 0) {
      if (this.bulkDeleteInterval) clearInterval(this.bulkDeleteInterval);
      if (!this.bulkDeleteTaskId) return;
      let intervalTime = 8000;
      if (sessionIdsLength >= 100) {
        intervalTime = 30000;
      } else if (sessionIdsLength >= 50) {
        intervalTime = 20000;
      } else if (sessionIdsLength >= 20) {
        intervalTime = 15000;
      }
      this.bulkDeleteInterval = setInterval(() => {
        this.GET_TASK_STATUS({ task_id: this.bulkDeleteTaskId })
          .then((response) => {
            const status = response?.detail?.response?.status;
            if (status === "completed" || status === "success") {
              clearInterval(this.bulkDeleteInterval);
              this.showSnackbar("選択した通話を削除しました。", "success");
              this.selectedCalls = [];
              this.bulkDeleteTaskId = "";
              setTimeout(() => {
                this.isBulkDeleting = false;
                this.deleteBulkDialog = false;
                this.refreshCallList();
              }, 1000);
            } else if (status === "failed" || status === "cancelled") {
              clearInterval(this.bulkDeleteInterval);
              this.bulkDeleteInterval = null;
              this.bulkDeleteTaskId = "";
              this.isBulkDeleting = false;
            }
          })
          .catch((error) => {
            clearInterval(this.bulkDeleteInterval);
            this.isBulkDeleting = false;
            console.error("Error polling bulk delete status:", error);
          });
      }, intervalTime);
    },

    deleteCallFunction() {
      this.deleteDialog = false;
      if (this.selectedSessionId) {
        this.DELETE_CALL_DETAIL({ session_id: this.selectedSessionId })
          .then((res) => {
            if (res) {
              this.showSnackbar("通話が正常に削除されました。", "success");
            }
            setTimeout(() => {
              this.refreshCallList();
            }, 1000);
          })
          .catch((error) => {
            console.error("Error deleting call:", error);
          });
      } else {
        this.showSnackbar("Session ID is not available", "error");
      }
    },

    handleSearch(e) {
      if (!e.shiftKey && e.keyCode === 13 && !this.isComposing) {
        e.preventDefault();
        this.refreshCallList();
        interactionUpdate(INTERACTION_POINT.APPLY_FILTER, "global_search", e.target.value || "default");
      }
    },

    copyToClipboard(text) {
      if (navigator.clipboard && navigator.clipboard.writeText) {
        navigator.clipboard.writeText(text).then(
          () => {
            this.showSnackbar("テキストがクリップボードにコピーされました。", "success");
          },
          (err) => {
            console.error("Error copying text: ", err);
            this.showSnackbar("Error copying text to clipboard.", "error");
          }
        );
      } else {
        console.error("Clipboard API not supported");
        this.showSnackbar("Clipboard functionality is not supported in this browser.", "error");
      }
    },

    downloadFile(item, index) {
      let key = item?.audio_file_path;
      if (!key) {
        return;
      }
      if (key.includes("contents/f73e2850-d046-48ef-9aea-ad3d4d46b2db/")) {
        key = key.replace(
          "contents/f73e2850-d046-48ef-9aea-ad3d4d46b2db/",
          `contents/${this.activeAccountId}/`
        );
      }
      this.genaratePresignedUrl(key, index, item);
    },

    genaratePresignedUrl(key, index, item) {
      this.$set(this.callIndexDownloadLoader, index, true);
      this.presignedUrlLoader = true;
      this.RETRIEVE_PRESIGNED_URL({ s3_key: key })
        .then((response) => {
          if (response) {
            window.open(response);
          }
          this.presignedUrlLoader = false;
          this.$set(this.callIndexDownloadLoader, index, false);
          interactionUpdate(INTERACTION_POINT.CALL_LIST_DOWNLOAD_AUDIO_FILE, `ダウンロードアイコン`, item.session_id || "default");
        })
        .catch((error) => {
          console.error("Error generating presigned url: ", error);
          this.presignedUrlLoader = false;
          this.$set(this.callIndexDownloadLoader, index, false);
        });
    },

    async openFilter() {
      if (this.openSearchFilter) {
        let prev_search_filter = this.cleanObject(this.search_filter);
        const hasKeyValue = Object.keys(prev_search_filter)?.length > 0;
        if (hasKeyValue) {
          this.search_filter = {};
          await this.$nextTick();
          this.refreshCallList();
        }
      }
      this.openSearchFilter = !this.openSearchFilter;
      await this.$nextTick();
      const divElement = document.querySelector(".filter-field-section");
      const divHeight = divElement ? divElement.offsetHeight : 0;
      const tbodyElement = document.querySelector(".call-table tbody");
      if (tbodyElement) {
        if (divHeight > 0) {
          const calcValue = `calc(100vh - (210px + ${divHeight}px))`;
          tbodyElement.style.setProperty("height", calcValue, "important");
        } else {
          tbodyElement.style.removeProperty("height");
        }
      }
    },

    cleanObject(obj) {
      if (typeof obj !== "object" || obj === null) return obj;
      const entries = Object.entries(obj)
        .map(([key, value]) => {
          if (value === null || value === undefined) return null;
          if (typeof value === "string") {
            if (value.trim() === "") return null;
            return [key, value];
          }
          if (typeof value === "object") {
            const cleaned = this.cleanObject(value);
            if (cleaned && typeof cleaned === "object" && Object.keys(cleaned).length === 0) {
              return null;
            }
            return [key, cleaned];
          }
          return [key, value];
        })
        .filter(Boolean);
      return Object.fromEntries(entries);
    },

    mergeDateTimeFilters(updatedFilters) {
      const result = { ...updatedFilters };
      const mergeField = (dateStr, timeStr) => {
        if (!dateStr || !timeStr) return null;
        const [h, m] = timeStr.split(":");
        const paddedTime = `${h.padStart(2, "0")}:${m.padStart(2, "0")}`;
        return `${dateStr}T${paddedTime}:00`;
      };

      if (result.date && result.call_time) {
        const startDateTime = mergeField(result.date.start_date, result.call_time.start_date);
        const endDateTime = mergeField(result.date.end_date, result.call_time.end_date);
        result.call_time = {};
        if (startDateTime) result.call_time.start_date = startDateTime;
        if (endDateTime) result.call_time.end_date = endDateTime;
        delete result.date;
      } else if (result.date) {
        result.call_time = {};
        if (result.date.start_date) result.call_time.start_date = result.date.start_date;
        if (result.date.end_date) result.call_time.end_date = result.date.end_date;
        delete result.date;
      }
      Object.keys(result).forEach((key) => {
        if (
          result[key] == null ||
          (typeof result[key] === "object" && Object.keys(result[key]).length === 0)
        ) {
          delete result[key];
        }
      });
      return result;
    },

    async handleInputChange(updatedFilters) {
      let search_filter = this.cleanObject(updatedFilters);
      if (
        (search_filter.date?.start_date && search_filter.date?.end_date) ||
        (search_filter.call_time?.start_date && search_filter.call_time?.end_date)
      ) {
        search_filter = this.mergeDateTimeFilters(search_filter);
      }
      this.search_filter = search_filter;
      await this.$nextTick();
      this.debouncedProcess(search_filter);
      this.debounceInteractionUpdate(search_filter);
    },

    pageRangePagination(page, perPage, total) {
      if (total <= 0)
        return `全 ${total} 件中 ${total}  件を表示\u00A0 1ページあたり `;
      const start = (page - 1) * perPage + 1;
      const end = Math.min(page * perPage, total);
      return `全 ${total} 件中  ${start}-${end} 件を表示\u00A0 1ページあたり `;
    },
  },
};
